import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { calendarAPI, projectsAPI } from '../lib/api';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const PRIORITY_COLORS = {
  high: 'bg-red-500',
  medium: 'bg-amber-500',
  low: 'bg-blue-500',
};

export default function CalendarView() {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [projectFilter, setProjectFilter] = useState('all');
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    const fetchTasks = async () => {
      try {
        const [res, projRes] = await Promise.all([calendarAPI.getTasks(), projectsAPI.list()]);
        setTasks(res.data);
        setProjects(projRes.data);
      } catch (err) {
        console.error('Failed to fetch tasks:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchTasks();
  }, []);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const monthName = currentDate.toLocaleString('en-US', { month: 'long', year: 'numeric' });

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  const today = () => setCurrentDate(new Date());

  const getTasksForDay = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return tasks.filter(t => {
      if (!t.due_date || !t.due_date.startsWith(dateStr)) return false;
      const matchSearch = !searchTerm || t.title?.toLowerCase().includes(searchTerm.toLowerCase()) || t.project_name?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchPriority = priorityFilter === 'all' || t.priority === priorityFilter;
      const matchProject = projectFilter === 'all' || t.project_id === projectFilter;
      return matchSearch && matchPriority && matchProject;
    });
  };

  const isToday = (day) => {
    const now = new Date();
    return day === now.getDate() && month === now.getMonth() && year === now.getFullYear();
  };

  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <Layout>
      <div className="space-y-4" data-testid="calendar-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <h1 className="page-title">Calendar</h1>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={prevMonth} data-testid="prev-month"><ChevronLeft className="h-4 w-4" /></Button>
            <Button variant="outline" size="sm" onClick={today}>Today</Button>
            <span className="text-lg font-medium min-w-[180px] text-center" data-testid="current-month">{monthName}</span>
            <Button variant="outline" size="icon" onClick={nextMonth} data-testid="next-month"><ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>

        {/* Search & Filter */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search tasks on calendar..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="calendar-search-input"
                />
              </div>
              <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                <SelectTrigger className="w-full sm:w-36" data-testid="calendar-priority-filter">
                  <SelectValue placeholder="Priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priority</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
              <Select value={projectFilter} onValueChange={setProjectFilter}>
                <SelectTrigger className="w-full sm:w-44" data-testid="calendar-project-filter">
                  <SelectValue placeholder="All Projects" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Projects</SelectItem>
                  {projects.map(p => (
                    <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="h-96 animate-pulse bg-muted rounded" />
            ) : (
              <div className="grid grid-cols-7">
                {days.map(d => (
                  <div key={d} className="p-2 text-center text-xs font-medium text-muted-foreground border-b border-r last:border-r-0">{d}</div>
                ))}
                {cells.map((day, idx) => {
                  const dayTasks = day ? getTasksForDay(day) : [];
                  return (
                    <div key={day ? `day-${day}` : `empty-${idx}`} className={`min-h-[100px] border-b border-r p-1.5 ${idx % 7 === 6 ? 'border-r-0' : ''} ${!day ? 'bg-muted/20' : ''} ${isToday(day) ? 'bg-primary/5' : ''}`}>
                      {day && (
                        <>
                          <span className={`text-xs font-medium ${isToday(day) ? 'bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center' : 'text-muted-foreground'}`}>
                            {day}
                          </span>
                          <div className="mt-1 space-y-0.5">
                            {dayTasks.slice(0, 3).map(t => (
                              <div
                                key={t.task_id}
                                className="text-[10px] px-1.5 py-0.5 rounded cursor-pointer hover:opacity-80 truncate bg-muted"
                                onClick={() => navigate(`/tasks?task=${t.task_id}`)}
                                title={`${t.title} - ${t.project_name}`}
                                data-testid={`cal-task-${t.task_id}`}
                              >
                                <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 ${PRIORITY_COLORS[t.priority] || 'bg-gray-400'}`} />
                                {t.title}
                              </div>
                            ))}
                            {dayTasks.length > 3 && (
                              <span className="text-[10px] text-muted-foreground pl-1">+{dayTasks.length - 3} more</span>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
